// First-Time User Experience (FTUE) step definitions, per role.
//
// Each step is a declarative card the ProductTour component renders.
// Kept role-specific so the welcome flow matches the user's actual
// daily tasks instead of being a generic feature dump.
//
// Shape of a step:
//   {
//     icon:       SVG-path JSX (no emoji — passes the skill's
//                 'no-emoji-icons' rule and renders consistently
//                 across iOS / Android / Windows).
//     color:      semantic accent color for this step's icon tile
//                 (h.* theme tokens preferred where possible).
//     title:      short noun phrase, 2–4 words.
//     body:       1–2 sentence description, plain English, mobile-
//                 first reading (under 35 chars per line).
//     cta?:       optional override for the next-button label on
//                 this step (otherwise we use the global default).
//   }
//
// Add or reorder steps freely — ProductTour reads .length to drive
// the progress dots and last-step CTA.

// Reusable SVG path fragments. Defined here so each step is a
// plain data object — no JSX imports needed in App.js logic.
// Each fragment expects to be rendered inside a <svg> with
// stroke="currentColor", strokeWidth="2", strokeLinecap="round".
const ICONS = {
  wave: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4L8 16" />
      <path d="M4 20h6v-6" />
      <path d="M4 20l12-12" />
    </>
  ),
  home: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  building: (
    <>
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </>
  ),
  rupee: (
    <>
      <path d="M6 3h12" />
      <path d="M6 8h12" />
      <path d="M6 13l9 8" />
      <path d="M9 13c0-2.5 2-5 5-5" />
      <path d="M9 13h5" />
    </>
  ),
  wrench: (
    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  msg: (
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  ),
  list: (
    <>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </>
  ),
  check: (
    <>
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  map: (
    <>
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </>
  ),
};

// Color picker — uses theme tokens where caller can pass them;
// otherwise falls back to a curated palette so steps still feel
// branded in dark mode.
//
// Caller passes `h` (theme) into the role getter so we can prefer
// h.ac / h.gn / h.wn / h.pp for the icon tile fill.
function paletteFor(h = {}) {
  return [
    h.ac || "#3b82f6",   // primary brand blue
    h.gn || "#16a34a",   // growth green
    h.pp || "#8b5cf6",   // accent purple
    h.wn || "#d97706",   // warm orange
    "#0ea5e9",            // sky for variety
    "#ef4444",            // red sparingly (used for chat / urgency)
  ];
}

export function getLandlordSteps(h, t) {
  const c = paletteFor(h);
  return [
    {
      icon: ICONS.wave,
      color: c[0],
      title: "Welcome aboard!",
      body: "Here's a 60-second tour of your landlord dashboard. You can skip this any time.",
      cta: "Start Tour",
    },
    {
      icon: ICONS.building,
      color: c[1],
      title: "Manage Properties",
      body: "Add your rental properties with photos, rent, and amenities. Edit anytime from the dashboard.",
    },
    {
      icon: ICONS.users,
      color: c[2],
      title: "Invite Tenants",
      body: "Send WhatsApp invites so tenants can log in, pay rent, and chat with you directly.",
    },
    {
      icon: ICONS.rupee,
      color: c[3],
      title: "Track Rent Payments",
      body: "See who's paid, who's pending, and accept payments online via UPI or cards.",
    },
    {
      icon: ICONS.wrench,
      color: c[4],
      title: "Maintenance",
      body: "Tenants raise requests. Assign service persons, track progress, and approve payouts.",
    },
    {
      icon: ICONS.chart,
      color: c[5],
      title: "Reports & Insights",
      body: "Monthly income, occupancy, and exportable CSVs. Find them in the gear menu under Reports.",
      cta: "Get Started",
    },
  ];
}

export function getTenantSteps(h, t) {
  const c = paletteFor(h);
  return [
    {
      icon: ICONS.wave,
      color: c[0],
      title: "Welcome home",
      body: "Quick tour of your tenant portal. You can skip and come back via the menu anytime.",
      cta: "Start Tour",
    },
    {
      icon: ICONS.home,
      color: c[1],
      title: "My House",
      body: "See your rental details, lease info, and the bills coming up this month.",
    },
    {
      icon: ICONS.rupee,
      color: c[3],
      title: "Pay Rent in 3 Taps",
      body: "UPI, cards, or net banking — pick the option you prefer. Receipt downloads automatically.",
    },
    {
      icon: ICONS.wrench,
      color: c[4],
      title: "Report Issues",
      body: "Tap Maintenance to send a request with photos. Your landlord is notified instantly.",
    },
    {
      icon: ICONS.msg,
      color: c[5],
      title: "Chat with Your Landlord",
      body: "Direct chat — no SMS fees, no phone-tag. Use it for quick questions any time.",
      cta: "Get Started",
    },
  ];
}

export function getServicePersonSteps(h, t) {
  const c = paletteFor(h);
  return [
    {
      icon: ICONS.wave,
      color: c[0],
      title: "Welcome to the team",
      body: "Quick tour of your service-person dashboard. Skip anytime — it's all in the menu.",
      cta: "Start Tour",
    },
    {
      icon: ICONS.list,
      color: c[1],
      title: "Job Board",
      body: "See maintenance requests from nearby landlords. Accept the ones you want.",
    },
    {
      icon: ICONS.check,
      color: c[3],
      title: "Complete Jobs",
      body: "Update status, add notes, and upload before/after photos. Landlord approves and pays.",
    },
    {
      icon: ICONS.rupee,
      color: c[4],
      title: "Track Earnings",
      body: "All payouts and completed jobs in one place. Filter by month, export when you need.",
      cta: "Get Started",
    },
  ];
}

// Default export — pick the right step set by role.
export default function stepsForRole(role, h, t) {
  if (role === "landlord") return getLandlordSteps(h, t);
  if (role === "tenant") return getTenantSteps(h, t);
  if (role === "service") return getServicePersonSteps(h, t);
  return [];
}
